import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from '../../i18n';
import type { UploadTask } from '../../upload-queue';

const fmtSize = (b: number): string => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * <cadence-upload-queue> — progress panel for a batch of uploads. Presentational: it
 * renders the task list passed in via `.tasks` and emits `cancel`/`retry`/`clear`
 * intents for the host (which owns the UploadQueue) to act on.
 */
@customElement('cadence-upload-queue')
export class CadenceUploadQueue extends LitElement {
  @property({ attribute: false }) tasks: UploadTask[] = [];

  static styles = css`
    :host { display: block; }
    .panel { border: 1px solid var(--color-border); border-radius: var(--radius-md); padding: 10px 12px; background: var(--color-bg-primary); }
    .head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
    .head .title { font-weight: 600; font-size: 0.9rem; }
    .head .sub { color: var(--color-text-secondary); font-size: 0.8rem; font-weight: 400; margin-left: 6px; }
    .clear { background: none; border: none; color: var(--color-primary); cursor: pointer; font: inherit; font-size: 0.82rem; padding: 2px 4px; }
    .row { display: flex; align-items: center; gap: 10px; padding: 6px 0; }
    .row + .row { border-top: 1px solid var(--color-border); }
    .info { flex: 1; min-width: 0; }
    .name { font-size: 0.84rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .bar { height: 6px; border-radius: 999px; background: var(--color-bg-secondary); overflow: hidden; margin-top: 5px; }
    .bar > span { display: block; height: 100%; background: var(--color-primary); transition: width 0.15s ease; }
    .bar.done > span { background: var(--color-success, #22c55e); }
    .bar.error > span { background: var(--color-danger, #ef4444); }
    .meta { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
    .status { font-size: 0.78rem; color: var(--color-text-secondary); min-width: 56px; text-align: right; }
    .status.error { color: var(--color-danger, #ef4444); }
    .status.done { color: var(--color-success, #16a34a); }
    .act { background: none; border: none; cursor: pointer; color: var(--color-text-secondary); font: inherit; font-size: 0.8rem; padding: 2px 6px; border-radius: var(--radius-sm, 6px); }
    .act:hover { color: var(--color-text-primary); background: var(--color-bg-secondary); }
    .act.retry { color: var(--color-primary); }
  `;

  private emit(name: string, id?: string) {
    this.dispatchEvent(new CustomEvent(name, { detail: id }));
  }

  private statusLabel(task: UploadTask): string {
    switch (task.status) {
      case 'queued': return t('ui.upload.status.queued');
      case 'preparing': return t('ui.upload.status.preparing');
      case 'uploading': return `${Math.round(task.progress * 100)}%`;
      case 'done': return t('ui.upload.status.done');
      case 'cancelled': return t('ui.upload.status.cancelled');
      case 'error': return t('ui.upload.status.failed');
    }
  }

  render() {
    if (!this.tasks.length) return '';
    const active = this.tasks.filter((t) => t.status === 'queued' || t.status === 'preparing' || t.status === 'uploading').length;
    const done = this.tasks.filter((t) => t.status === 'done').length;
    const hasFinished = this.tasks.some((t) => t.status === 'done' || t.status === 'error' || t.status === 'cancelled');
    return html`
      <div class="panel">
        <div class="head">
          <span class="title">${t('ui.upload.title')} <span class="sub">${t('ui.upload.doneCount', { done, total: this.tasks.length })}${active ? ` · ${t('ui.upload.inProgress', { active })}` : ''}</span></span>
          ${hasFinished ? html`<button class="clear" @click=${() => this.emit('clear')}>${t('ui.upload.clearFinished')}</button>` : ''}
        </div>
        ${this.tasks.map((task) => {
          const cancellable = task.status === 'queued' || task.status === 'preparing' || task.status === 'uploading';
          const width = task.status === 'done' ? 100 : task.status === 'error' || task.status === 'cancelled' ? 100 : Math.round(task.progress * 100);
          const barClass = task.status === 'done' ? 'done' : task.status === 'error' || task.status === 'cancelled' ? 'error' : '';
          return html`
            <div class="row">
              <div class="info">
                <div class="name" title=${task.file.name}>${task.file.name} <span class="sub" style="color:var(--color-text-secondary);font-size:0.78rem">${fmtSize(task.file.size)}</span></div>
                <div class="bar ${barClass}"><span style="width:${width}%"></span></div>
              </div>
              <div class="meta">
                <span class="status ${task.status === 'error' ? 'error' : task.status === 'done' ? 'done' : ''}" title=${task.error ?? ''}>${this.statusLabel(task)}</span>
                ${cancellable ? html`<button class="act" title=${t('ui.upload.cancel')} @click=${() => this.emit('cancel', task.id)}>×</button>` : ''}
                ${task.status === 'error' ? html`<button class="act retry" @click=${() => this.emit('retry', task.id)}>${t('ui.upload.retry')}</button>` : ''}
              </div>
            </div>`;
        })}
      </div>`;
  }
}
